import { FAR } from "./render";

// The rules of the runner, with no React and no DOM: a game is a plain object,
// and step() moves it on by one frame and says what happened. ArticleRunner.jsx
// plays it in the app; script/build_article_runner_html.js inlines this same
// file into the standalone page, so the two can't drift apart.

// Seconds a gate takes to reach you: generous at first, tightening with every
// right answer and easing off after a crash.
export const START_SECONDS = 4.5;
export const MIN_SECONDS = 1.8;
const SPEED_UP = 1.04;
const SLOW_DOWN = 0.88;

// After a right answer the noun you just got stays on screen while it's said
// aloud, and the next gate waits - otherwise you hear the last word while
// reading the next one.
export const ANSWER_HOLD = 0.9;

export const LIVES = 3;
const CRASH_PAUSE = 2.2;
// A missed noun comes back this many gates later, while it's still fresh.
const RETRY_GAP = 4;

// The dash: commit early and the gate rushes to you. It's a bet - a right
// answer earns a bonus for however far off the gate still was, a wrong one
// costs a life like any other. Waiting is still always allowed.
export const DASH_SECONDS = 0.18;
export const DASH_BONUS = 10;
// Too close to be worth it: below this the dash would only be an animation.
const DASH_MIN_Z = 1;

// The server checks submitted scores against these, so keep
// Labs::ArticleRunnerController's MAX_POINTS_PER_ANSWER and
// MIN_SECONDS_PER_ANSWER in step: 30 + a full dash bonus, and the answer hold
// plus a dash fired the instant a gate appears.
export const MAX_POINTS_PER_ANSWER = 30 + DASH_BONUS;

// A right answer is worth 10, plus 2 for each right answer in a row before it,
// up to 30 from the eleventh in a row.
export function pointsFor(streak) {
  return 10 + 2 * Math.min(streak, 10);
}

// Up to DASH_BONUS, for how much of the track the gate still had to cover.
export function dashBonus(fromZ) {
  return Math.max(0, Math.min(DASH_BONUS, Math.round((fromZ / FAR) * DASH_BONUS)));
}

export function shuffle(items) {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// Start in the middle lane, or the left of the middle two.
export function newGame(deck, articles) {
  const startLane = Math.floor((articles.length - 1) / 2);

  return {
    articles,
    bestStreak: 0,
    correct: 0,
    dash: null,
    dashes: 0,
    deck,
    distance: 0,
    elapsed: 0,
    gates: [],
    history: [],
    hold: 0,
    lane: startLane,
    laneX: startLane,
    lives: LIVES,
    missed: [],
    over: false,
    particles: [],
    pause: 0,
    paused: false,
    queue: shuffle(deck),
    score: 0,
    shake: 0,
    speed: FAR / START_SECONDS,
    streak: 0,
    time: 0,
  };
}

// An idle game for the track behind the start screen.
export function idleGame(articles) {
  return { ...newGame([], articles), over: true };
}

export function spawnGate(game) {
  if (!game.queue.length) game.queue = shuffle(game.deck);

  game.gates.push({ chosen: null, noun: game.queue.shift(), result: null, z: FAR });
}

export function currentGate(game) {
  return game.gates.find((gate) => !gate.result) || null;
}

// What the HUD shows. The answered noun stays up through the hold after a
// right answer, until the next gate appears.
export function hud(game) {
  const gate = currentGate(game);
  return {
    answered: game.hold > 0 ? game.lastAnswered : null,
    lives: game.lives,
    noun: gate ? gate.noun : null,
    score: game.score,
    streak: game.streak,
  };
}

function playing(game) {
  return !game.over && !game.paused;
}

// Returns true if the runner moved. Locked during a dash - you've committed.
export function moveTo(game, lane) {
  if (!playing(game) || game.dash) return false;

  const next = Math.max(0, Math.min(game.articles.length - 1, lane));
  if (next === game.lane) return false;

  game.lane = next;
  return true;
}

// Returns true if a dash started.
export function startDash(game) {
  if (!playing(game) || game.dash) return false;

  const gate = currentGate(game);
  if (!gate || gate.z < DASH_MIN_Z) return false;

  game.dash = { fromZ: gate.z, gate, startedAt: game.time };
  // Outlives the dash itself, so the leap can land after the gate is passed.
  game.lastDashAt = game.time;
  game.dashes += 1;
  return true;
}

function resolve(game, gate, events) {
  const answer = game.articles.indexOf(gate.noun.article);
  const dashed = game.dash && game.dash.gate === gate;
  gate.chosen = game.lane;

  if (game.lane === answer) {
    const bonus = dashed ? dashBonus(game.dash.fromZ) : 0;
    const points = pointsFor(game.streak) + bonus;
    gate.result = "correct";
    game.score += points;
    game.streak += 1;
    game.bestStreak = Math.max(game.bestStreak, game.streak);
    game.correct += 1;
    game.history.push(gate.noun.article);
    game.speed = Math.min(FAR / MIN_SECONDS, game.speed * SPEED_UP);
    game.hopAt = game.time;
    game.hold = ANSWER_HOLD;
    game.lastAnswered = gate.noun;
    events.push({ answer, bonus, correct: true, gate, points, type: "resolved" });
  } else {
    gate.result = "wrong";
    game.lives -= 1;
    game.streak = 0;
    game.history.push("miss");
    game.missed.push(gate.noun);
    game.queue.splice(RETRY_GAP - 1, 0, gate.noun);
    game.speed = Math.max(FAR / START_SECONDS, game.speed * SLOW_DOWN);
    game.shake = 0.4;
    game.pause = CRASH_PAUSE;
    game.paused = true;
    events.push({ answer, chosen: game.articles[game.lane], correct: false, gate, type: "resolved" });
  }

  if (dashed) game.dash = null;
}

// The run, once it's over, for the game over screen, sharing and the leaderboard.
// A noun missed twice is still one noun to learn.
export function summary(game) {
  const missed = [];
  game.missed.forEach((noun) => {
    if (!missed.some((seen) => seen.word === noun.word)) missed.push(noun);
  });

  return {
    bestStreak: game.bestStreak,
    correct: game.correct,
    dashes: game.dashes,
    history: game.history,
    missed,
    score: game.score,
    seconds: Math.round(game.elapsed),
  };
}

// Moves the game on by dt seconds. Returns what happened, in order:
//   { type: "resolved", correct, gate, answer, points, bonus } - a gate reached you
//   { type: "crashOver" } - the pause after a wrong answer ended
//   { type: "spawned" }   - a new gate is on the track
//   { type: "over" }      - out of lives
export function step(game, dt) {
  const events = [];
  game.time += dt;
  game.laneX += (game.lane - game.laneX) * Math.min(1, dt * 16);
  game.shake = Math.max(0, game.shake - dt);

  if (game.over) {
    game.distance += dt * 3;
    return events;
  }

  game.elapsed += dt;

  if (game.paused) {
    game.pause -= dt;
    if (game.pause > 0) return events;

    game.paused = false;
    game.gates = game.gates.filter((gate) => gate.result !== "wrong");
    events.push({ type: "crashOver" });
    if (game.lives <= 0) {
      game.over = true;
      events.push({ type: "over" });
      return events;
    }
    spawnGate(game);
    events.push({ type: "spawned" });
    return events;
  }

  if (game.hold > 0) {
    game.hold -= dt;
    if (game.hold <= 0) {
      spawnGate(game);
      events.push({ type: "spawned" });
    }
  }

  // A dash covers the gate's remaining distance in DASH_SECONDS, scenery and all.
  const speed = game.dash ? Math.max(game.speed, game.dash.fromZ / DASH_SECONDS) : game.speed;
  const travelled = speed * dt;
  game.distance += travelled;
  game.gates.forEach((gate) => {
    gate.z -= travelled;
    if (!gate.result && gate.z <= 0) resolve(game, gate, events);
  });
  game.gates = game.gates.filter((gate) => gate.z > -1.5 || gate.result === "wrong");

  return events;
}
